import React, { useEffect, useRef, useState } from "react";
import Icon from 'components/Safra/Icon';

import { getAirports, getMostSearchedAirports, getTopResearches} from "../../../_services/airport.service";
import useDebounce from "../../../contexts/useDebounce";
import { useTranslation } from "../../../contexts/Safra/TranslationContext";
import {
    trackLocationFieldFocus,
    trackLocationSearch,
    trackLocationSelected,
    trackSuggestionDisplay
} from "../../../utils/analytics";
import {
    setOriginAirport,
    setDestinationAirport,
    clearOriginAirport,
    clearDestinationAirport
} from "../../../utils/airportStorage";

let sharedMostSearchedPromise = null;
let sharedTopResearchesPromise = null;

function SearchAirport({
    disabled = false,
    location,
    tipo,
    id,
    externalError,
    onClearExternalError,
    defaultValue = '',
    groupPosition = 'single',
    className = ''
}) {

    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [displayValue, setDisplayValue] = useState(defaultValue)
    const [open, setOpen] = useState(false)
    const [list, setList] = useState([])
    const [lastResearchs, setLastResearchs] = useState([])
    const [topSearches, setTopSearches] = useState([])
    const [origemCode, setOrigemCode] = useState('')
    const [destinoCode, setDestinoCode] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const [hasDuplicateConflict, setHasDuplicateConflict] = useState(false)
    const [validationError, setValidationError] = useState(false)

    const { t } = useTranslation();
    const inputRef = useRef(null);
    const hasValue = !!(displayValue && displayValue.trim().length);

    const debouncedChange = useDebounce(setSearch, 500, setLoading);

    const labelText = tipo === 'origem' ? t('safra.home.fields.origin') : t('safra.home.fields.destination');

    const IconPlaneOrigem = () => (
        <span style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '20px', minHeight: '20px'}}><Icon name="plane" size={20} color="black" /></span>
    );

    const IconPlaneDestino = () => (
        <span style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '20px', minHeight: '20px'}}><Icon name="plane" size={20} color="black" style={{transform: "rotate(90deg)"}} /></span>
    );

    const CloseIcon = () => (
        <span style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '20px', minHeight: '20px'}}><Icon name="x" size={20} color="#02003C" /></span>
    );

    const ClockIcon = () => (
        <span style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '16px', minHeight: '16px'}}><Icon name="clock" size={16} color="black" /></span>
    );

    const TopDestinationsIcon = () => (
        <span style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '16px', minHeight: '16px'}}><Icon name="building-2" size={16} color="black" /></span>
    );

    const handleChange = (e) => {
        const value = e.target.value;
        setLoading(true);

        if (!value || value.length < 3) {
            setValidationError(false);
        }

        if (isFocused) {
            setOpen(true);
            if (value.length >= 3) {
                trackLocationSearch('flight', tipo, value);
            }
        } else {
            setOpen(false);
        }

        setDisplayValue(value);
        setHasDuplicateConflict(false);
        debouncedChange(value);
    }

    const clearSelection = () => {
        setDisplayValue('');
        setList([]);
        setHasDuplicateConflict(false);
        setOpen(false);
        setValidationError(false);
        onClearExternalError?.();

        if (tipo === 'origem') {
            setOrigemCode('');
            clearOriginAirport();
            window.dispatchEvent(new CustomEvent('safra-airport-change', { detail: { tipo: 'origem' } }));
        } else {
            setDestinoCode('');
            clearDestinationAirport();
            window.dispatchEvent(new CustomEvent('safra-airport-change', { detail: { tipo: 'destino' } }));
        }

        if (inputRef.current) {
            inputRef.current.focus();
        }
    }

    const formatAirports = (data) => {
        const result = [];
        data.forEach(location => {
            result.push({
                code: location.main_code,
                name: location.name,
                country: location.country_code,
                type: location.is_city ? 'city' : 'airport',
                cityCode: location.code,
                value: location.value,
                mainCode: location.main_code,
                cityName: location.city_name,
                isCity: location.is_city,
            });
        });

        return result;
    }

    useEffect(() => {
        const searchLocation = async () => {
            setLoading(true)
            const res = await getAirports(displayValue);

            if (!res || !res.data) {
                setLoading(false);
                return;
            }

            const formatted = formatAirports(res.data);

            const fromCode = (localStorage.getItem("from-code") || localStorage.getItem("from-main-code") || '').toLowerCase();
            const fromCity = (localStorage.getItem("from-city-code") || '').toLowerCase();
            const toCode = (localStorage.getItem("to-code") || localStorage.getItem("to-main-code") || '').toLowerCase();
            const toCity = (localStorage.getItem("to-city-code") || '').toLowerCase();

            let duplicateConflict = false;
            const filtered = formatted.filter(item => {
                const code = item.code.toLowerCase();
                const city = (item.cityCode || '').toLowerCase();

                if (tipo === 'destino') {
                    if (fromCode && code === fromCode) {
                        duplicateConflict = true;
                        return false;
                    }
                    if (fromCity && city === fromCity) {
                        duplicateConflict = true;
                        return false;
                    }
                }

                if (tipo === 'origem') {
                    if (toCode && code === toCode) {
                        duplicateConflict = true;
                        return false;
                    }
                    if (toCity && city === toCity) {
                        duplicateConflict = true;
                        return false;
                    }
                }

                return true;
            });

            setHasDuplicateConflict(duplicateConflict);
            setList(filtered);

            if (filtered.length > 0) {
                const cities = filtered.filter(item => item.type === 'city');
                const airports = filtered.filter(item => item.type === 'airport');
                const hasCategories = cities.length > 0 && airports.length > 0;
                trackSuggestionDisplay('flight', tipo, filtered.length, hasCategories);
            }
        }

        if (search.length >= 3) {
            searchLocation().then(() => {
                setLoading(false)
            })
        } else {
            setList([]);
            setHasDuplicateConflict(false);
            setLoading(false);
        }

    }, [search])

    useEffect(() => {
        async function getResearchs() {
            if (!sharedMostSearchedPromise) {
                sharedMostSearchedPromise = getMostSearchedAirports({ 
                    customer_uuid: localStorage.getItem('customer_parent') || '' 
                }).then(res => {
                    return res?.data;
                }).finally(() => {
                    sharedMostSearchedPromise = null;
                });
            }
            const data = await sharedMostSearchedPromise;

            if (data) {
                const list = tipo === 'origem' ? data.departures : data.arrivals;
                const mapped = (list || []).map(it => ({
                    code: it.main_code || it.code || '',
                    name: it.name || it.value || '',
                    country: it.country_code || 'BR',
                    type: it.is_city ? 'city' : 'airport',
                    cityCode: it.code || '',
                    value: it.value || it.name || '',
                    mainCode: it.main_code || it.code || '',
                    cityName: it.city_name || it.city_name || '',
                    isCity: it.is_city || false
                })).filter(x => x.code && x.name);
                setLastResearchs(mapped);
            }
        }
        getResearchs();
    }, [tipo])

    useEffect(() => {
        let active = true;

        async function getTopSearchesFromApi() {
            const cacheKey = 'top-researches-airports';
            const cacheDateKey = 'top-researches-airports-date';
            const today = new Date().toISOString().split('T')[0];

            let data = null;
            const cachedDate = sessionStorage.getItem(cacheDateKey);
            const cachedData = sessionStorage.getItem(cacheKey);

            if (cachedDate === today && cachedData) {
                try {
                    data = JSON.parse(cachedData);
                } catch (e) {
                    console.error('Error parsing cached top researches data', e);
                }
            }

            try {
                if (!data) {
                    if (!sharedTopResearchesPromise) {
                        sharedTopResearchesPromise = getTopResearches().then(res => {
                            if (res?.data) {
                                sessionStorage.setItem(cacheKey, JSON.stringify(res.data));
                                sessionStorage.setItem(cacheDateKey, today);
                                return res.data;
                            }
                            return null;
                        }).finally(() => {
                            sharedTopResearchesPromise = null;
                        });
                    }
                    data = await sharedTopResearchesPromise;
                }

                if (data && active) {
                    const list = tipo === 'origem' ? data.departures : data.arrivals;
                    const mapped = (list || []).map(it => ({
                        code: it.main_code || it.code || '',
                        name: it.name || it.value || '',
                        country: it.country_code || 'BR',
                        type: it.is_city ? 'city' : 'airport',
                        cityCode: it.code || '',
                        value: it.value || it.name || '',
                        mainCode: it.main_code || it.code || '',
                        cityName: it.city_name || it.city_name || '',
                        isCity: it.is_city || false
                    })).filter(x => x.code && x.name);

                    const uniqueMapped = mapped.filter((item, index, array) =>
                        index === array.findIndex(other => other.code === item.code && other.name === item.name)
                    );

                    setTopSearches(uniqueMapped.slice(0, 3));
                }
            } catch (error) {
                if (active) {
                    setTopSearches([]);
                }
            }
        }

        getTopSearchesFromApi();

        return () => {
            active = false;
        };
    }, [tipo])

    const changeLocation = (params) => {
        setDisplayValue(params.name)
        setValidationError(false);

        const locationType = params.type || 'airport';

        if (tipo === 'origem') {
            setOrigemCode(params.code)
            setOriginAirport({
                value: params.value,
                name: params.name,
                code: params.code,
                mainCode: params.mainCode,
                countryCode: params.country,
                cityCode: params.cityCode || '',
                cityName: params.cityName || '',
                isCity: params.isCity || false
            });
            window.dispatchEvent(new CustomEvent('safra-airport-change', { detail: { tipo: 'origem' } }));
        } else {
            setDestinoCode(params.code)
            setDestinationAirport({
                value: params.value,
                name: params.name,
                code: params.code,
                mainCode: params.mainCode,
                countryCode: params.country,
                cityCode: params.cityCode || '',
                cityName: params.cityName || '',
                isCity: params.isCity || false
            });
            window.dispatchEvent(new CustomEvent('safra-airport-change', { detail: { tipo: 'destino' } }));
        }

        trackLocationSelected('flight', tipo, params.code, params.name, locationType);

        setOpen(false)
    }

    const handleBlur = () => {
        setIsFocused(false);

        setTimeout(() => {
            setOpen(false);
            const currentValue = (inputRef.current?.value || '').trim();

            const storedName = tipo === 'origem'
                ? localStorage.getItem("from-name")
                : localStorage.getItem("to-name");

            if (currentValue && storedName) {
                const decodedStoredName = decodeURIComponent(storedName);
                if (currentValue.length >= 3 && currentValue !== decodedStoredName) {
                    setValidationError(true);
                }
            } else if (currentValue && !storedName && currentValue.length >= 3) {
                setValidationError(true);
            } else {
                setValidationError(false);
            }
        }, 80);
    }

    const handleFocus = () => {
        setIsFocused(true);
        setValidationError(false);
        onClearExternalError?.();
        setOpen(true);
        trackLocationFieldFocus('flight', tipo);
    }

    useEffect(() => {
        if (externalError) {
            setValidationError(true);
        }
    }, [externalError]);

    useEffect(() => {
        setDisplayValue(defaultValue || '');
        setSearch('');
        setList([]);
        setHasDuplicateConflict(false);
        setValidationError(false);
        setLoading(false);
    }, [defaultValue]);

    const shouldShowDropdown = open && isFocused;
    const borderColor = validationError ? '#DC3545' : '#D8D8D8';

    const resolveFieldBorder = () => {
        if (groupPosition === 'top') {
            return {
                borderTop: `1px solid ${borderColor}`,
                borderLeft: `1px solid ${borderColor}`,
                borderRight: `1px solid ${borderColor}`,
                borderRadius: '4px 4px 0 0',
            };
        }

        if (groupPosition === 'bottom') {
            return {
                border: `1px solid ${borderColor}`,
                borderRadius: '0 0 4px 4px',
            };
        }

        return {
            border: `1px solid ${borderColor}`,
            borderRadius: '4px',
        };
    };

    return (
        <div className={`dropdown ${className}`.trim()}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    minHeight: '62px',
                    padding: '8px 60px 8px 16px',
                    ...resolveFieldBorder(),
                }}
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px', minWidth: '20px', minHeight: '20px' }}>
                    {tipo === 'origem' ? <IconPlaneOrigem /> : <IconPlaneDestino />}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: hasValue ? '4px' : '0px', flex: 1, minWidth: 0, position: 'relative' }}>
                    {hasValue && (
                        <label
                            style={{
                                color: '#4F4F4F',
                                letterSpacing: '-0.8px',
                                margin: 0,
                                lineHeight: '1',
                                fontSize: '12px',
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 400,
                                marginBottom: hasValue ? '0px' : '4px'
                            }}
                        >
                            {labelText}
                        </label>
                    )}
                    <input
                        ref={inputRef}
                        className={`form-control form-control-custom safra-airport-input ${validationError ? 'is-invalid' : ''}`}
                        value={displayValue}
                        defaultValue={location?.name}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onClick={() => setOpen(true)}
                        placeholder={!hasValue ? labelText : ""}
                        disabled={disabled}
                        id={id}
                        autoComplete="off"
                        role="combobox"
                        aria-label={labelText}
                        aria-expanded={open && isFocused}
                        aria-controls={`${id}-list`}
                        aria-haspopup="listbox"
                        aria-autocomplete="list"
                        style={{
                            border: 'none',
                            boxShadow: 'none',
                            padding: 0,
                            height: !hasValue ? 'auto' : '20px',
                            fontSize: '14px',
                            fontWeight: !hasValue ? 400 : 600,
                            fontFamily: 'Montserrat, sans-serif',
                            color: '#4F4F4F',
                            WebkitTextFillColor: '#4F4F4F',
                            background: 'transparent',
                            textOverflow: 'ellipsis'
                        }}
                    />
                </div>
                {displayValue && isFocused && (
                    <button
                        type="button"
                        aria-label={t('safra.components.search_airport.clear')}
                        onMouseDown={(e) => { e.preventDefault(); clearSelection(); }}
                        className="btn btn-link p-0 m-0"
                        style={{ color: '#0B0D40', minWidth: '20px', position: 'absolute', right: 15, top: '50%', transform: 'translateY(-50%)'}}
                    >
                        <CloseIcon />
                    </button>
                )}
            </div>

            <ul
                className={`dropdown-menu w-100 ${shouldShowDropdown && 'show'}`}
                style={{ maxHeight: '260px', overflowY: 'auto' }}
                onMouseDown={(e) => e.preventDefault()}
                id={`${id}-list`}
                role="listbox"
                aria-label={t('safra.components.search_airport.searching')}
            >
                {
                    loading ? <li className="text-left" style={{ padding: '32px' }}>{t('safra.components.search_airport.searching')}</li> :
                        (() => {
                            if (search.length < 3) {
                                if (!lastResearchs?.length && !topSearches?.length) return null;

                                return (
                                    <>
                                        {lastResearchs?.length > 0 && (
                                            <>
                                                <li
                                                    style={{
                                                        background: '#f7f7f7',
                                                        padding: '12px 16px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        fontSize: '12px',
                                                        color: '#000',
                                                        letterSpacing: '-0.8px',
                                                        fontWeight: 400
                                                    }}
                                                >
                                                    <ClockIcon />
                                                    {t('safra.components.search_airport.recent_searches')}
                                                </li>
                                                {lastResearchs.map(item => (
                                                    <li style={{ marginTop: '24px' }} key={item.code}>
                                                        <a className="dropdown-item text-wrap" href="#"
                                                            role="option"
                                                            aria-selected={displayValue === item.name}
                                                            style={{
                                                                fontSize: '14px',
                                                                color: '#000',
                                                                fontWeight: 400,
                                                                padding: '0 16px',
                                                                letterSpacing: '-0.8px'
                                                            }}
                                                            onClick={(e) => { e.preventDefault(); changeLocation(item) }}>{item.name}</a>
                                                    </li>
                                                ))}
                                            </>
                                        )}

                                        {topSearches?.length > 0 && (
                                            <>
                                                <li
                                                    style={{
                                                        background: '#f7f7f7',
                                                        padding: '12px 16px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        fontSize: '12px',
                                                        color: '#000',
                                                        letterSpacing: '-0.8px',
                                                        fontWeight: 400,
                                                        marginTop: lastResearchs?.length > 0 ? '24px' : '0px'
                                                    }}
                                                >
                                                    <TopDestinationsIcon />
                                                    {tipo === 'origem' ? t('safra.components.search_airport.trending_origins') : t('safra.components.search_airport.trending_destinations')}
                                                </li>
                                                {topSearches.map((item, index) => (
                                                    <li style={{ marginTop: '24px' }} key={`${item.code}-${index}`}>
                                                        <a className="dropdown-item text-wrap" href="#"
                                                            role="option"
                                                            aria-selected={displayValue === item.name}
                                                            style={{
                                                                fontSize: '14px',
                                                                color: '#000',
                                                                fontWeight: 400,
                                                                padding: '0 16px',
                                                                letterSpacing: '-0.8px'
                                                            }}
                                                            onClick={(e) => { e.preventDefault(); changeLocation(item) }}>{item.name}</a>
                                                    </li>
                                                ))}
                                            </>
                                        )}
                                    </>
                                )
                            }

                            if (!list.length) {
                                if (hasDuplicateConflict) {
                                    return (
                                        <li
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '12px 24px',
                                                color: '#00003C',
                                                border: '1px solid rgba(0,0,0,0.25)',
                                                background: '#fff'
                                            }}
                                        >
                                            <Icon name="info" size={20} />
                                            <span style={{ fontSize: '14px', letterSpacing: '-0.8px' }}>
                                                {tipo === 'destino'
                                                    ? t('safra.components.search_airport.duplicate_destination_error')
                                                    : t('safra.components.search_airport.duplicate_origin_error')}
                                            </span>
                                        </li>
                                    )
                                }
                                return <li className="text-center" style={{ padding: '32px' }}>{t('safra.components.search_airport.no_results')}</li>
                            }

                            const cities = list.filter(item => item.type === 'city');
                            const airports = list.filter(item => item.type === 'airport');
                            return (
                                <>
                                    {cities.length > 0 && (
                                        <>
                                            <li
                                                style={{
                                                    background: '#f7f7f7',
                                                    padding: '12px 16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    fontSize: '12px',
                                                    color: '#000',
                                                    letterSpacing: '-0.8px',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                <Icon name="building-2" size={16} />
                                                {t('safra.components.search_airport.cities')}
                                            </li>
                                            {cities.map(item => (
                                                <li style={{ marginTop: '24px' }} key={item.code}>
                                                    <a className="dropdown-item text-wrap" href="#"
                                                        role="option"
                                                        aria-selected={displayValue === item.name}
                                                        style={{
                                                            fontSize: '14px',
                                                            color: '#000',
                                                            fontWeight: 400,
                                                            padding: '0 16px',
                                                            letterSpacing: '-0.8px'
                                                        }}
                                                        onClick={(e) => { e.preventDefault(); changeLocation(item) }}>{item.name}</a>
                                                </li>
                                            ))}
                                        </>
                                    )}

                                    {airports.length > 0 && (
                                        <>
                                            <li
                                                style={{
                                                    background: '#f7f7f7',
                                                    padding: '12px 16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    fontSize: '12px',
                                                    color: '#000',
                                                    letterSpacing: '-0.8px',
                                                    fontWeight: 400,
                                                    marginTop: cities?.length > 0 ? '24px' : '0px'
                                                }}
                                            >
                                                <Icon name="plane" size={16} />
                                                {t('safra.components.search_airport.airports')}
                                            </li>
                                            {airports.map(item => (
                                                <li style={{ marginTop: '24px' }} key={item.code}>
                                                    <a className="dropdown-item text-wrap" href="#"
                                                        role="option"
                                                        aria-selected={displayValue === item.name}
                                                        style={{
                                                            fontSize: '14px',
                                                            color: '#000',
                                                            fontWeight: 400,
                                                            padding: '0 16px',
                                                            letterSpacing: '-0.8px'
                                                        }}
                                                        onClick={(e) => { e.preventDefault(); changeLocation(item) }}>{item.name}</a>
                                                </li>
                                            ))}
                                        </>
                                    )}
                                </>
                            )
                        })()
                }
            </ul>

            {validationError && (
                <div
                    style={{
                        fontSize: '12px',
                        color: '#dc3545',
                        marginTop: '4px',
                        letterSpacing: '-0.8px',
                    }}
                >
                    {t('safra.components.search_airport.validation_error')}
                </div>
            )}
        </div>
    )
}

export default SearchAirport;
